import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react'
import axioscore from 'axios'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { AuthContext } from './AuthContext'
import { useNavigate } from 'react-router-dom'

const axios = axioscore.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000, // 30 second timeout
})

// Add request interceptor to include auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('userToken')
    if (token) {
      config.headers.Authorization = `Bearer ${JSON.parse(token).token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor to handle errors
axios.interceptors.response.use(
  (response) => response,

  (error) => {
    if (error.response?.status === 401) {
      localStorage.clear()
      toast.error(error.response.data.message)
      window.location.href = '/'
    }
    return Promise.reject(error)
  }
)

const BASE_URL = process.env.REACT_APP_BASE_URL

const AppContext = createContext()

const AppProvider = ({ children }) => {
  const { userToken, setIsLoggedIn, isLoggedIn, setuserToken } = useContext(AuthContext)

  const [isLoading, setIsLoading] = useState(false)

  const logout = useCallback(() => {
    setIsLoggedIn(false)
    localStorage.clear()
    return 'success'
  }, [setIsLoggedIn])

  const verifySession = useCallback(async () => {
    if (userToken) {
      try {
        const response = await axios.post(`${BASE_URL}/user/verifysession`, {
          userid: userToken.userid,
        })

        if (response.data.message === 'success') {
          localStorage.setItem('userToken', JSON.stringify(response.data.userToken[0]))
          const token = JSON.parse(localStorage.getItem('userToken'))
          setuserToken(token)
        } else {
          logout()
        }
      } catch (error) {
        console.error('Verify Failed: ' + error)
        logout()
      }
    }
  }, [userToken, setuserToken, logout])

  useEffect(() => {
    const interval = setInterval(verifySession, 60000 * 1)
    return () => clearInterval(interval)
  }, [userToken])

  const getdashboard = useCallback(async () => {
    try {
      setIsLoading(true)
      const userToken = JSON.parse(localStorage.getItem('userToken'))
      const response = await axios.post(`${BASE_URL}/dashboard/stats`, {
        userid: userToken.userid,
        usertype: userToken.type,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getmembers = useCallback(async (statusfilter) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/members/all`, { statusfilter })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getmember = useCallback(async (userid) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/members/get`, {
        userid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const reviewmember = useCallback(async ({ userid, reviewaction }) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/members/review`, {
        userid,
        reviewaction,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Member Account ' + reviewaction + ' Successfully!')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const addmember = useCallback(async ({ name, email, password, confirmpassword, isGuest }) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/members/add`, {
        name,
        email,
        password,
        confirmpassword,
        isGuest,
      })

      if (response.data.message === 'success') {
        toast.success('Member added successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updatemember = useCallback(async ({ name, email, status, isGuest, userId }) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/members/update`, {
        name,
        email,
        status,
        isGuest,
        userid: userId,
      })

      if (response.data.message === 'success') {
        toast.success('Member updated successfully')
        setIsLoading(false)
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deletemember = useCallback(async (userid) => {
    try {
      const userToken = JSON.parse(localStorage.getItem('userToken'))
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/members/delete`, {
        userid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Member deleted successfully')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    }
    setIsLoading(false)
  }, [])

  const getadmins = useCallback(async (statusfilter) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/admins/all`, { statusfilter })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getadmin = useCallback(async (userid) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/admins/get`, {
        userid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const addadmin = useCallback(async ({ name, email, password, confirmpassword, isGuest }) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/admins/add`, {
        name,
        email,
        password,
        confirmpassword,
        isGuest,
      })

      if (response.data.message === 'success') {
        toast.success('Administrator added successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updateadmin = useCallback(async ({ name, email, status, isGuest, userId }) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/admins/update`, {
        name,
        email,
        status,
        isGuest,
        userid: userId,
      })

      if (response.data.message === 'success') {
        toast.success('Administrator updated successfully')
        setIsLoading(false)
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deleteadmin = useCallback(async (userid) => {
    try {
      const userToken = JSON.parse(localStorage.getItem('userToken'))
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/admins/delete`, {
        userid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Administrator deleted successfully')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    }
    setIsLoading(false)
  }, [])

  const getmatchdays = useCallback(async (competition) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matches/matchdays/all`, { competition: competition })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getdaymatches = useCallback(async (matchday, competition) => {
    try {
      const response = await axios.post(`${BASE_URL}/matches/matchdays/matches`, { matchday, competition })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getmatches = useCallback(async (statusfilter) => {
    try {
      setIsLoading(true)
      const userToken = JSON.parse(localStorage.getItem('userToken'))
      const response = await axios.post(`${BASE_URL}/matches/all`, { statusfilter, userid: userToken.userid })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getmatch = useCallback(async (matchid) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matches/get`, {
        matchid,
      })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getrecentmatches = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matches/recent`)

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const reviewmatch = useCallback(async ({ matchid, reviewaction, reviewnote }) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matches/review`, {
        matchid,
        reviewaction,
        reviewnote,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Work ' + reviewaction + ' successfully!')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Works are posted as multipart so an optional cover image can ride along
  const workFormData = (work) => {
    const data = new FormData()
    for (const key of ['competition', 'title', 'author', 'language', 'episodes', 'date', 'time', 'matchday', 'status', 'matchid']) {
      if (work[key] !== undefined && work[key] !== null) data.append(key, work[key])
    }
    if (work.cover) data.append('cover', work.cover)
    return data
  }

  const addmatch = useCallback(async (work) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matches/add`, workFormData(work), {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (response.data.message === 'success') {
        toast.success('Work added successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.message || error.response?.data || error.message))
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updatematch = useCallback(async (work) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matches/update`, workFormData(work), {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      if (response.data.message === 'success') {
        toast.success('Work updated successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.message || error.message))
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deletematch = useCallback(async (matchid) => {
    try {
      const userToken = JSON.parse(localStorage.getItem('userToken'))
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matches/delete`, {
        matchid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Work deleted successfully')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    }
    setIsLoading(false)
  }, [])

  const getlinkcategories = useCallback(async (type, matchid) => {
    try {
      const response = await axios.post(`${BASE_URL}/linkcategories/all`, { type, matchid })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getlinkcategory = useCallback(async (catid) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/linkcategories/get`, {
        catid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const addlinkcategory = useCallback(async (name) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/linkcategories/add`, {
        name,
      })

      if (response.data.message === 'success') {
        toast.success('Record added successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updatelinkcategory = useCallback(async (name, catid) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/linkcategories/update`, {
        name,
        catid,
      })

      if (response.data.message === 'success') {
        toast.success('Record updated successfully')
        setIsLoading(false)
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deletelinkcategory = useCallback(async (catid) => {
    try {
      const userToken = JSON.parse(localStorage.getItem('userToken'))
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/linkcategories/delete`, {
        catid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Record deleted successfully')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    }
    setIsLoading(false)
  }, [])

  const getmatchdaysdata = useCallback(async (competition) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matchdaydata/all`, { competition })
      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getmatchdaydata = useCallback(async (matchday, competition) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matchdaydata/get`, {
        matchday,
        competition,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getmatchdayclosurerates = useCallback(async (matchday, competition) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matchdaydata/getclosure`, {
        matchday,
        competition,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updatematchdaydata = useCallback(
    async (
      matchday,
      competition,
      twitterNotified,
      twitterSuspended,
      tiktokNotified,
      tiktokSuspended,
      telegramNotified,
      telegramSuspended,
      telegramNonSuspended,
      discordSuspended,
      snapchatNotified,
      telegramImpacted,
      closureRates,
      highlightRates,
      closureTimes,
      highlightTimes
    ) => {
      try {
        setIsLoading(true)
        const response = await axios.post(`${BASE_URL}/matchdaydata/update`, {
          matchday,
          competition,
          twitterNotified,
          twitterSuspended,
          tiktokNotified,
          tiktokSuspended,
          telegramNotified,
          telegramSuspended,
          telegramNonSuspended,
          discordSuspended,
          snapchatNotified,
          telegramImpacted,
          closureRates,
          highlightRates,
          closureTimes,
          highlightTimes,
        })

        if (response.data.message === 'success') {
          toast.success('Record updated successfully')
          setIsLoading(false)
          return response.data.message
        } else {
          toast.error(response.data.message)
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed: ' + error)
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const getwebsitelinks = useCallback(async () => {
    try {
      const response = await axios.post(`${BASE_URL}/linkwebsites/all`)

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getwebsitelink = useCallback(async (webid) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/linkwebsites/get`, {
        webid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const addwebsitelink = useCallback(async (name, link = '', iconFile = null) => {
    try {
      setIsLoading(true)
      const formData = new FormData()
      formData.append('name', name)
      formData.append('link', link)
      if (iconFile) {
        formData.append('icon', iconFile)
      }

      const response = await axios.post(`${BASE_URL}/linkwebsites/add`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.message === 'success') {
        toast.success('Record added successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])


  const updatewebsitelink = useCallback(async (name, link, webid, iconFile = null) => {
    try {
      setIsLoading(true)
      const formData = new FormData()
      formData.append('name', name)
      formData.append('link', link)
      formData.append('webid', webid)
      if (iconFile) {
        formData.append('icon', iconFile)
      }

      const response = await axios.post(`${BASE_URL}/linkwebsites/update`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.message === 'success') {
        toast.success('Record updated successfully')
        setIsLoading(false)
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deletewebsitelink = useCallback(async (webid) => {
    try {
      const userToken = JSON.parse(localStorage.getItem('userToken'))
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/linkwebsites/delete`, {
        webid,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Record deleted successfully')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    }
    setIsLoading(false)
  }, [])

  const addmatchlinks = useCallback(async (input, type, matchid, addedat) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matchlinks/add`, {
        input,
        type,
        matchid,
        addedat,
      })

      if (response.data.message === 'success') {
        toast.success('Record added successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getmatchlinks = useCallback(async (type, matchid) => {
    try {
      const response = await axios.post(`${BASE_URL}/matchlinks/all`, {
        type,
        matchid,
      })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const downloadmatchlinksexcel = useCallback(async (matchid, filenameHint) => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      const response = await axios.post(
        `${BASE_URL}/matchlinks/export`,
        { matchid, timezone },
        { responseType: 'blob' }
      )

      let filename = filenameHint ? `${filenameHint}-Infringing-Links.xlsx` : 'Infringing-Links.xlsx'
      const contentDisposition = response.headers['content-disposition']
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/)
        if (match && match[1]) filename = match[1]
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      return 'success'
    } catch (error) {
      toast.error('Failed: ' + error)
    }
  }, [])

  const downloadmatchdayreportexcel = useCallback(async (matchday, competition, filenameHint) => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      const response = await axios.post(
        `${BASE_URL}/reports/matchday/export`,
        { matchday, competition, timezone },
        { responseType: 'blob' }
      )

      let filename = filenameHint ? `${filenameHint}-Report.xlsx` : 'Period-Report.xlsx'
      const contentDisposition = response.headers['content-disposition']
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/)
        if (match && match[1]) filename = match[1]
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      return 'success'
    } catch (error) {
      toast.error('Failed: ' + error)
    }
  }, [])

  const getdeletedmatchlinks = useCallback(async (matchid) => {
    try {
      const response = await axios.post(`${BASE_URL}/matchlinks/deleted`, {
        matchid,
      })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getmatchlinkswebsites = useCallback(async (matchid) => {
    try {
      const response = await axios.post(`${BASE_URL}/matchlinks/websites`, {
        matchid,
      })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getmatchwebsites = useCallback(async (matchid) => {
    try {
      const response = await axios.post(`${BASE_URL}/matches/websites`, {
        matchid,
      })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getspecwebsites = useCallback(async (matchid) => {
    try {
      const response = await axios.post(`${BASE_URL}/matches/specwebsites`, {
        matchid,
      })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const updatematchspectators = useCallback(async (matchid, spectators) => {
    try {
      const response = await axios.post(`${BASE_URL}/matches/websites/update`, {
        matchid,
        spectators,
      })

      if (response.data.message === 'success') {
        toast.success('Record updated successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const setmatchlinkstakendown = useCallback(async (ids, deletedat) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matchlinks/takendown`, {
        ids,
        deletedat,
      })

      if (response.data.message === 'success') {
        toast.success(ids.length > 1 ? 'Take down time set for selected links' : 'Take down time set')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const setmatchlinkstakendownbulk = useCallback(async (items) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matchlinks/takendown`, {
        items,
      })

      if (response.data.message === 'success') {
        toast.success(items.length > 1 ? 'Take down time set for selected links' : 'Take down time set')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deletematchlink = useCallback(async (id, deletedat) => {
    return setmatchlinkstakendown([id], deletedat)
  }, [setmatchlinkstakendown])

  const deletematchlinks = useCallback(
    async (ids) => {
      const deletedat = new Date().toISOString().slice(0, 19) || null
      return setmatchlinkstakendown(ids, deletedat)
    },
    [setmatchlinkstakendown]
  )

  const removematchlinks = useCallback(async (ids) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/matchlinks/remove`, { ids })
      if (response.data.message === 'success') {
        toast.success(ids.length > 1 ? 'Links removed' : 'Link removed')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getoverview = useCallback(async (competition, date) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/overview/all`, { competition, date })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getoverviewstats = useCallback(async (competition, date, team) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/overview/stats`, { competition, date, team })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getoverviewdomain = useCallback(async (competition, date, team) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/overview/domain`, { competition, date, team })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getreportmatchdays = useCallback(async (competition) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/reports/matchdays/all`, { competition })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getoverviewdaymatches = useCallback(async (matchday, competition) => {
    try {
      const response = await axios.post(`${BASE_URL}/reports/matchdays/matches`, { matchday, competition })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getoverviewdaywebsites = useCallback(async (matchday, competition) => {
    try {
      const response = await axios.post(`${BASE_URL}/reports/links/websites`, { matchday, competition })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getoverviewmatchdaydata = useCallback(async (matchday, competition) => {
    try {
      const response = await axios.post(`${BASE_URL}/reports/matchday/data`, { matchday, competition })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const getoverviewmatchdaydatatotals = useCallback(async (matchday, competition) => {
    try {
      const response = await axios.post(`${BASE_URL}/reports/matchday/totals`, { matchday, competition })

      if (response.data.message === 'success') {
        return response.data.result
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
    }
  }, [])

  const updateprofile = useCallback(async (name, email) => {
    try {
      const userToken = JSON.parse(localStorage.getItem('userToken'))
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/user/update`, {
        name,
        email,
        userid: userToken.userid,
      })

      if (response.data.message === 'success') {
        localStorage.setItem('userToken', JSON.stringify(response.data.userToken[0]))
        toast.success('Profile updated successfully')
        setIsLoading(false)
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Request Failed: ' + error)
    }
    setIsLoading(false)
  }, [])

  const updatepassword = useCallback(async (oldpassword, newpassword, confirmpassword) => {
    try {
      if (newpassword === confirmpassword) {
        const userToken = JSON.parse(localStorage.getItem('userToken'))
        setIsLoading(true)
        const response = await axios.post(`${BASE_URL}/user/updatepassword`, {
          oldpassword,
          newpassword,
          confirmpassword,
          userid: userToken.userid,
        })

        if (response.data.message === 'success') {
          toast.success('Password updated successfully')
          setIsLoading(false)
          return 'success'
        } else {
          toast.error(response.data.message)
        }
      } else {
        toast.error('Passwords do not match')
      }
    } catch (error) {
      toast.error('Request Failed: ' + error)
    }
    setIsLoading(false)
  }, [])

   const getCompetitions = useCallback(async ({ includeTeams } = { includeTeams: false }) => {
     try {
       setIsLoading(true)
       const response = await axios.post(`${BASE_URL}/competitions/all`, {
         includeTeams,
         orderBy: 'desc',
       })

       if (response.data.message === 'success') {
         return response.data.result
       } else {
         toast.error(response.data.message)
       }
     } catch (error) {
       toast.error('Failed: ' + error)
     } finally {
       setIsLoading(false)
     }
   }, [])

  const createCompetition = useCallback(async ({ name, status, file, startDate, endDate }) => {
    try {
      setIsLoading(true)

      const formData = new FormData()
      formData.append('name', name)
      formData.append('status', status)
      formData.append('file', file)
      formData.append('startDate', startDate)
      formData.append('endDate', endDate)

      const response = await axios.post(`${BASE_URL}/competitions/create`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.message === 'success') {
        toast.success('Category created successfully')
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updateCompetition = useCallback(async ({ name, status, file, competitionId, startDate, endDate }) => {
    try {
      setIsLoading(true)

      const formData = new FormData()
      formData.append('name', name)
      formData.append('status', status)
      formData.append('file', file)
      formData.append('competitionId', competitionId)
      formData.append('startDate', startDate)
      formData.append('endDate', endDate)

      const response = await axios.post(`${BASE_URL}/competitions/update`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.message === 'success') {
        toast.success('Category updated successfully')
        setIsLoading(false)
        return response.data.message
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deleteCompetition = useCallback(async (competitionId) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/competitions/delete`, {
        competitionId,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Category deleted successfully')
        return 'success'
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error('Failed: ' + error)
    }
    setIsLoading(false)
  }, [])

  // Infringing URLs management functions
  const getInfringingUrls = useCallback(async (page = 1, limit = 10) => {
    try {
      setIsLoading(true)
      const response = await axios.get(`${BASE_URL}/infringing-urls`, {
        params: { page, limit },
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return {
          urls: response.data.urls || [],
          pagination: response.data.pagination || {},
        }
      } else {
        toast.error(response.data.message || 'Failed to fetch infringing URLs')
        return { urls: [], pagination: {} }
      }
    } catch (error) {
      setIsLoading(false)
      toast.error('Failed to fetch infringing URLs: ' + error.message)
      return { urls: [], pagination: {} }
    }
  }, [])

  const addInfringingUrls = useCallback(async (urls) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/infringing-urls`, { urls })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success(`Successfully submitted ${response.data.result.inserted} URLs!`)
        return response.data.result
      } else {
        toast.error(response.data.message || 'Failed to submit URLs')
        return null
      }
    } catch (error) {
      setIsLoading(false)
      toast.error('Failed to submit URLs: ' + error.message)
      return null
    }
  }, [])

  const deleteInfringingUrl = useCallback(async (id) => {
    try {
      const response = await axios.delete(`${BASE_URL}/infringing-urls/${id}`)
      return response.data
    } catch (error) {
      toast.error('Failed to delete infringing URL: ' + error.message)
    }
  }, [])

  // Allow Listed Accounts management functions (mirrors bein-sports-ui)
  const getAllowListedAccounts = useCallback(async (page = 1, limit = 10, search = '') => {
    try {
      setIsLoading(true)
      const response = await axios.get(`${BASE_URL}/allow_listed_accounts`, {
        params: { page, limit, search },
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        return {
          accounts: response.data.accounts || [],
          pagination: response.data.pagination || {},
        }
      } else {
        toast.error(response.data.message || 'Failed to fetch allow listed accounts')
        return { accounts: [], pagination: {} }
      }
    } catch (error) {
      setIsLoading(false)
      toast.error('Failed to fetch allow listed accounts: ' + error.message)
      return { accounts: [], pagination: {} }
    }
  }, [])

  const addAllowListedAccounts = useCallback(async ({ urls, lic_from, lic_to, tag }) => {
    try {
      setIsLoading(true)
      const response = await axios.post(`${BASE_URL}/allow_listed_accounts`, {
        urls,
        lic_from,
        lic_to,
        tag,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success(`Successfully added ${response.data.result.inserted} account(s)!`)
        return response.data.result
      } else {
        toast.error(response.data.message || 'Failed to add allow listed accounts')
        return null
      }
    } catch (error) {
      setIsLoading(false)
      toast.error('Failed to add allow listed accounts: ' + error.message)
      return null
    }
  }, [])

  const updateAllowListedAccount = useCallback(async ({ id, url, lic_from, lic_to, tag }) => {
    try {
      setIsLoading(true)
      const response = await axios.put(`${BASE_URL}/allow_listed_account/${id}`, {
        url,
        lic_from,
        lic_to,
        tag,
      })

      if (response.data.message === 'success') {
        setIsLoading(false)
        toast.success('Allow listed account updated successfully')
        return response.data
      } else {
        toast.error(response.data.message || 'Failed to update allow listed account')
        return null
      }
    } catch (error) {
      setIsLoading(false)
      toast.error('Failed to update allow listed account: ' + error.message)
      return null
    }
  }, [])

  const deleteAllowListedAccount = useCallback(async (id) => {
    try {
      const response = await axios.delete(`${BASE_URL}/allow_listed_account/${id}`)
      if (response.data.message === 'success') {
        toast.success('Allow listed account deleted successfully')
        return response.data
      } else {
        toast.error(response.data.message || 'Failed to delete allow listed account')
      }
    } catch (error) {
      toast.error('Failed to delete allow listed account: ' + error.message)
    }
  }, [])

  const exportAllowListedAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/allow_listed_accounts/export`)
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'allow_listed_accounts.csv'
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error('Failed to export allow listed accounts: ' + error.message)
      return null
    }
  }, [])

  const value = useMemo(() => {
    return {
      setIsLoading,
      userToken,
      isLoading,
      logout,
      getdashboard,
      getmembers,
      getmember,
      reviewmember,
      addmember,
      updatemember,
      deletemember,
      getadmins,
      getadmin,
      addadmin,
      updateadmin,
      deleteadmin,
      getmatchdays,
      getdaymatches,
      getmatches,
      getmatch,
      getrecentmatches,
      addmatch,
      reviewmatch,
      updatematch,
      deletematch,
      getlinkcategories,
      getlinkcategory,
      addlinkcategory,
      updatelinkcategory,
      deletelinkcategory,
      getmatchdaysdata,
      getmatchdaydata,
      getmatchdayclosurerates,
      updatematchdaydata,
      getwebsitelinks,
      getwebsitelink,
      addwebsitelink,
      updatewebsitelink,
      deletewebsitelink,
      getmatchlinks,
      downloadmatchlinksexcel,
      downloadmatchdayreportexcel,
      getdeletedmatchlinks,
      getmatchlinkswebsites,
      getmatchwebsites,
      getspecwebsites,
      updatematchspectators,
      addmatchlinks,
      deletematchlink,
      setmatchlinkstakendown,
      setmatchlinkstakendownbulk,
      deletematchlinks,
      removematchlinks,
      getoverview,
      getoverviewdomain,
      getreportmatchdays,
      getoverviewdaymatches,
      getoverviewdaywebsites,
      getoverviewmatchdaydata,
      getoverviewmatchdaydatatotals,
      getoverviewstats,
      updateprofile,
      updatepassword,
      getCompetitions,
      createCompetition,
      updateCompetition,
      deleteCompetition,
      getInfringingUrls,
      addInfringingUrls,
      deleteInfringingUrl,
      getAllowListedAccounts,
      addAllowListedAccounts,
      updateAllowListedAccount,
      deleteAllowListedAccount,
      exportAllowListedAccounts,
    }
  }, [
    userToken,
    isLoading,
    logout,
    getdashboard,
    getmembers,
    getmember,
    reviewmember,
    addmember,
    updatemember,
    deletemember,
    getadmins,
    getadmin,
    addadmin,
    updateadmin,
    deleteadmin,
    getmatchdays,
    getdaymatches,
    getmatches,
    getmatch,
    getrecentmatches,
    addmatch,
    reviewmatch,
    updatematch,
    deletematch,
    getlinkcategories,
    getlinkcategory,
    addlinkcategory,
    updatelinkcategory,
    deletelinkcategory,
    getmatchdaysdata,
    getmatchdaydata,
    getmatchdayclosurerates,
    updatematchdaydata,
    getwebsitelinks,
    getwebsitelink,
    addwebsitelink,
    updatewebsitelink,
    deletewebsitelink,
    getmatchlinks,
    downloadmatchlinksexcel,
    downloadmatchdayreportexcel,
    getdeletedmatchlinks,
    getmatchlinkswebsites,
    getmatchwebsites,
    getspecwebsites,
    updatematchspectators,
    addmatchlinks,
    deletematchlink,
    setmatchlinkstakendown,
    setmatchlinkstakendownbulk,
    deletematchlinks,
    removematchlinks,
    getoverview,
    getoverviewdomain,
    getreportmatchdays,
    getoverviewdaymatches,
    getoverviewdaywebsites,
    getoverviewmatchdaydata,
    getoverviewmatchdaydatatotals,
    getoverviewstats,
    updateprofile,
    updatepassword,
    getCompetitions,
    createCompetition,
    updateCompetition,
    deleteCompetition,
    getInfringingUrls,
    addInfringingUrls,
    deleteInfringingUrl,
    getAllowListedAccounts,
    addAllowListedAccounts,
    updateAllowListedAccount,
    deleteAllowListedAccount,
    exportAllowListedAccounts,
    setIsLoading,
  ])

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export { AppContext, AppProvider }
